/**
 * Monitor Flow view: the agent dependency graph (DEC-188 decisions 2 to 5,
 * mock "Round 5 refined").
 *
 * Wave columns as soft bands, Intake / Planner / Git history on the left,
 * Critic and Report on the right, the shared signal as a glowing band. Every
 * line exists in the backend, and its state comes only from what the scan
 * recorded (see flow/edgeModel.ts). Clicking an agent brightens and labels its
 * lines, fades the rest, and lists what it receives and passes on.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { GitFork } from 'lucide-react'
import { useScanStore, type AgentState } from '../../stores/scanStore'
import { AGENT_CATEGORY_CODE, WAVE_AGENTS } from '../../types/api'
import {
  STATUS_COLOR,
  STATUS_MARK,
  agentDescription,
  agentStatusWord,
  buildWaves,
  finishedOk,
  hasStory,
  isDone,
  tokenColor,
  type AgentStatusKey,
} from './waveModel'
import { agentHeadline } from './failureModel'
import {
  PSEUDO,
  STATE_TEXT,
  anyNotRecorded,
  buildEdges,
  edgeLabel,
  type EdgeContext,
  type GraphEdge,
} from './flow/edgeModel'
import { CANVAS_H, CANVAS_W, NODE_H, NODE_W, SIGNAL_BAND, edgePath, layoutNodes, waveBands } from './flow/layout'
import { GRAPH_GLOW, GRAPH_KIND_LABEL, GRAPH_KIND_ORDER, edgeColor, useGraphPalette, type GraphPalette } from './flow/graphColors'

interface FlowViewProps {
  agents: Record<string, AgentState>
  scanId: string
  /** DEC-185: rows rebuilt from the saved scan (see FocusView). */
  fromSaved?: boolean
  /** The scan's state as the API reports it. A saved scan's store state stays 'queued',
   *  so this is what tells a finished scan from a failed one. */
  apiState?: string | null
  /** DEC-197: the Critic failed (it is in the scan's failed agents). Its node says so
   *  instead of "Done". */
  criticFailed?: boolean
  /** DEC-197: open the agent pop-up from the side panel. */
  onInspect?: (agentId: string) => void
}

export const NOT_RECORDED_NOTE = 'What each agent received was not recorded for this scan.'

const PSEUDO_LABEL: Record<string, string> = {
  [PSEUDO.intake]: 'Intake',
  [PSEUDO.planner]: 'Planner',
  [PSEUDO.history]: 'Git history',
  [PSEUDO.critic]: 'Critic',
  [PSEUDO.report]: 'Report',
  [PSEUDO.signal]: 'Shared findings signal',
}

const PSEUDO_META: Record<string, string> = {
  [PSEUDO.intake]: 'files, languages',
  [PSEUDO.planner]: 'who runs, on what',
  [PSEUDO.history]: 'commits, owners',
  [PSEUDO.critic]: 'dedup, groups, conflicts',
  [PSEUDO.report]: 'cost, Debt Index',
}

const WHAT: Record<string, string> = {
  [PSEUDO.intake]: 'Lists the files and languages in the repository and hands them to the Planner.',
  [PSEUDO.planner]: 'Decides who runs and on which files. Skipped agents are shown with the reason.',
  [PSEUDO.history]: 'Commits and owners mined from git, used by Architecture for knowledge risk.',
  [PSEUDO.signal]: 'Which files earlier agents flagged. Later agents read it inside their AI step only.',
  [PSEUDO.critic]:
    'Removes duplicates, groups similar findings, and settles conflicts: Dead Code wins over Complexity, Security over Performance, Architecture over Idiomatic.',
  [PSEUDO.report]: 'Turns the reviewed findings into cost and the Debt Index.',
  d7_performance:
    'Looks for slow patterns. In its AI step it reads the Coupling service map and the files earlier agents flagged, so it can focus on hot spots.',
  d4_architecture:
    'Checks layering and boundaries. It is the agent with the most inputs: git history, complex files from Complexity, and the shared signal.',
  d12_coupling:
    'Builds a map of services and their links from the code itself, and hands it to Performance. It does not read anything from Architecture.',
}

const WAVE_TAG: Record<number, string> = { 1: 'parallel', 2: 'parallel', 3: 'one after another', 4: '' }

/** Critic lines drawn even when nothing is selected: the ones with a clear path (see report). */
const ALWAYS_DRAWN_CRITIC_WAVES = [3, 4]

type NodeStatus = { key: AgentStatusKey | 'none'; label: string }

function stateColor(e: GraphEdge, palette: GraphPalette): string {
  if (e.state === 'passed') return edgeColor(e.kind, palette)
  if (e.state === 'not_used') return tokenColor('warn')
  if (e.state === 'order') return tokenColor('muted')
  return tokenColor('faint')
}

function stateLine(e: GraphEdge): string {
  return e.reason ? `${STATE_TEXT[e.state]}: ${e.reason}` : STATE_TEXT[e.state]
}

export function FlowView({
  agents,
  scanId,
  fromSaved = false,
  apiState = null,
  criticFailed = false,
  onInspect,
}: FlowViewProps) {
  // Fit the fixed-size canvas to its column (inner width minus the 18px padding each side).
  const fitRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  useEffect(() => {
    const el = fitRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const measure = () => {
      const avail = el.clientWidth - 36
      setScale(Math.max(0.7, Math.min(1, avail / CANVAS_W)))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  const scanState = useScanStore((s) => s.scanState)
  const criticSummary = useScanStore((s) => s.criticSummary)
  const deadFiles = useScanStore((s) => s.deadFiles ?? null)
  const palette = useGraphPalette()
  const [sel, setSel] = useState<string | null>(null)

  // A saved scan's store scanState stays at 'queued', so the API state decides: only a
  // complete or partial scan wrote a report. A failed one did not.
  const finalState = fromSaved ? apiState : scanState
  const scanFinished = finalState === 'complete' || finalState === 'partial'
  const criticDone = scanFinished || criticSummary !== null
  const criticRunning = !criticDone && scanState === 'critic'

  const edges = useMemo(() => {
    const ctx: EdgeContext = { agents, deadFiles, scanFinished, criticDone, fromSaved }
    return buildEdges(ctx, WAVE_AGENTS)
  }, [agents, deadFiles, scanFinished, criticDone, fromSaved])
  const pos = useMemo(() => layoutNodes(WAVE_AGENTS), [])
  const waves = buildWaves(agents)
  // The not-recorded note is about agent inputs; the Report's dead-file count is separate.
  const notRecorded = anyNotRecorded(edges.filter((e) => e.id !== 'd9_dead_code>report'))

  const nameOf = (id: string) => PSEUDO_LABEL[id] ?? agents[id]?.label ?? id

  const statusOf = (id: string): NodeStatus => {
    const a = agents[id]
    if (a) {
      if (fromSaved && a.status === 'waiting') return { key: 'waiting', label: 'Not run' }
      return { key: a.status as AgentStatusKey, label: agentStatusWord(a) }
    }
    if (id === PSEUDO.intake || id === PSEUDO.planner) {
      const e = edges.find((x) => x.id === 'intake>planner')
      return e?.state === 'passed' ? { key: 'complete', label: 'Done' } : { key: 'waiting', label: 'Waiting' }
    }
    if (id === PSEUDO.history) {
      const e = edges.find((x) => x.id === 'history>d4_architecture')
      if (e?.state === 'passed') return { key: 'complete', label: 'Used' }
      if (e?.state === 'not_used') return { key: 'none', label: e.reason === 'No git history' ? 'Not found' : 'Not used' }
      if (e?.state === 'not_recorded') return { key: 'waiting', label: 'Not recorded' }
      return { key: 'waiting', label: 'Waiting' }
    }
    if (id === PSEUDO.critic) {
      if (criticFailed) return { key: 'failed', label: 'Failed' }
      if (criticDone) return { key: 'complete', label: 'Done' }
      if (criticRunning) return { key: 'running', label: 'Running' }
      return { key: 'waiting', label: 'Waiting' }
    }
    if (id === PSEUDO.report) return scanFinished ? { key: 'complete', label: 'Done' } : { key: 'waiting', label: 'Waiting' }
    return { key: 'waiting', label: '' }
  }

  const touches = (e: GraphEdge, id: string | null) => !!id && (e.from === id || e.to === id)
  const adjacent = (id: string) =>
    !sel || id === sel || edges.some((e) => (e.from === sel && e.to === id) || (e.to === sel && e.from === id))

  const visible = edges.filter((e) => {
    if (e.to !== PSEUDO.critic) return true
    const wave = agents[e.from]?.wave ?? 0
    return ALWAYS_DRAWN_CRITIC_WAVES.includes(wave) || touches(e, sel) || sel === PSEUDO.critic
  })

  const pick = (id: string) => setSel((cur) => (cur === id ? null : id))

  const nodeIds = [...Object.keys(pos)].filter((id) => agents[id] || PSEUDO_LABEL[id])

  const glow = GRAPH_GLOW[palette.theme]
  const rootStyle = {
    '--flow-glow-a': glow.a,
    '--flow-glow-b': glow.b,
    '--flow-glow-c': glow.c,
    '--flow-glow-d': glow.d,
  } as CSSProperties

  const selEdgesIn = sel ? edges.filter((e) => e.to === sel) : []
  const selEdgesOut = sel ? edges.filter((e) => e.from === sel) : []
  const selAgent = sel ? agents[sel] : undefined
  const selCategory = sel ? AGENT_CATEGORY_CODE[sel] : undefined
  const canViewFindings = !!selAgent && finishedOk(selAgent.status) && !!scanId
  const selStory = !!selAgent && hasStory(selAgent)

  return (
    <div
      className="bg-panel border border-hairline rounded-md overflow-hidden"
      style={rootStyle}
      onKeyDown={(ev) => {
        if (ev.key === 'Escape') setSel(null)
      }}
    >
      <style>{`
        @keyframes tdFlowDash { to { stroke-dashoffset: -24; } }
        @keyframes tdFlowGlow {
          0%, 100% { box-shadow: 0 0 0 1px var(--flow-glow-a), 0 0 18px var(--flow-glow-b); }
          50% { box-shadow: 0 0 0 1px var(--flow-glow-c), 0 0 30px var(--flow-glow-d); }
        }
        .td-flow-run { stroke-dasharray: 8 4; animation: tdFlowDash 0.8s linear infinite; }
        .td-flow-glow { animation: tdFlowGlow 2s ease-in-out infinite; }
        .td-flow-pulse { animation: specPulse 1.4s ease-in-out infinite; }
      `}</style>

      <div className="px-[18px] py-[13px] border-b border-fill-subtle flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <GitFork className="w-[13px] h-[13px] text-muted" />
          <span className="font-semibold text-[10px] leading-none tracking-[2px] uppercase text-muted">Agent dependency graph</span>
        </div>
        <div className="flex flex-col gap-[6px] items-end">
          <div className="flex gap-4 flex-wrap justify-end text-[12px] text-body" data-testid="flow-legend">
            {GRAPH_KIND_ORDER.map((k) => (
              <span key={k} className="inline-flex items-center gap-2">
                <span
                  className="w-[22px] h-[3px] rounded-sm"
                  style={{ background: palette.kind[k], boxShadow: `0 0 6px ${palette.kind[k]}` }}
                />
                {GRAPH_KIND_LABEL[k]}
              </span>
            ))}
          </div>
          <div className="flex gap-4 flex-wrap justify-end text-[11px] text-muted">
            <span>Moving and glowing: data passed</span>
            <span>Dashed: not used this scan</span>
            <span>Faint: not reached yet</span>
            <span>Thin grey: runs next, no data</span>
            {notRecorded && <span>Soft solid: possible, not recorded</span>}
          </div>
        </div>
      </div>

      {notRecorded && (
        <div className="mx-5 mt-4 px-3 py-2 rounded-md border border-hairline bg-fill-subtle text-[12px] text-body" role="note">
          {NOT_RECORDED_NOTE}
        </div>
      )}

      <div className="p-5 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_300px] gap-4 items-start">
        {/* Canvas: drawn at a fixed size and scaled down to fit its column, so the whole
            graph is always visible. Below 70% it would be unreadable, so it scrolls instead. */}
        <div
          ref={fitRef}
          className="border border-hairline rounded-[18px] p-[18px] overflow-x-auto"
          style={{
            background: 'radial-gradient(90% 70% at 50% 0%, rgb(var(--accent) / 0.14), transparent 70%), rgb(var(--ground))',
          }}
        >
          <div style={{ width: CANVAS_W * scale, height: CANVAS_H * scale }}>
          <div className="relative origin-top-left" style={{ width: CANVAS_W, height: CANVAS_H, transform: scale < 1 ? `scale(${scale})` : undefined }}>
            {waveBands(WAVE_AGENTS).map((b) => {
              const w = waves.find((x) => x.num === b.num)
              const color =
                w?.status === 'running' ? tokenColor('mark-running') : w?.status === 'done' ? tokenColor('mark-success') : tokenColor('mark-idle')
              return (
                <div
                  key={b.num}
                  className="absolute rounded-[16px] border border-hairline"
                  style={{
                    left: b.x,
                    top: 8,
                    width: b.w,
                    height: b.h,
                    padding: '7px 10px',
                    zIndex: 0,
                    background: 'linear-gradient(180deg, rgb(var(--accent) / 0.12), rgb(var(--accent) / 0.02) 60%)',
                  }}
                >
                  <span className="font-semibold text-[10px] tracking-[0.16em] uppercase" style={{ color }}>
                    Wave {b.num}
                  </span>
                  {WAVE_TAG[b.num] && <span className="text-[10px] text-muted ml-[6px]">{WAVE_TAG[b.num]}</span>}
                </div>
              )
            })}

            <svg
              viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
              width={CANVAS_W}
              height={CANVAS_H}
              className="absolute left-0 top-0 pointer-events-none"
              style={{ overflow: 'visible', zIndex: 1 }}
              aria-hidden="true"
            >
              {visible.map((e) => {
                const geo = edgePath(e, pos)
                if (!geo) return null
                const hi = touches(e, sel)
                const order = e.kind === 'order'
                const kindColor = edgeColor(e.kind, palette)
                const color =
                  e.state === 'not_reached' ? 'var(--hairline-strong)' : order ? 'var(--edge-idle)' : kindColor
                const w = order ? 1.2 : hi ? 3.4 : e.state === 'passed' ? 2.4 : 1.8
                const op = sel && !hi ? 0.18 : e.state === 'not_used' ? 0.6 : e.state === 'not_recorded' ? 0.5 : 1
                return (
                  <g key={e.id} data-edge={e.id} data-state={e.state} opacity={op}>
                    <path
                      d={geo.d}
                      fill="none"
                      stroke={color}
                      strokeWidth={w}
                      strokeLinecap="round"
                      strokeDasharray={e.state === 'not_used' ? '4 6' : undefined}
                      className={e.state === 'passed' ? 'td-flow-run' : undefined}
                      style={e.state === 'passed' ? { filter: `drop-shadow(0 0 ${hi ? 6 : 3}px ${kindColor})` } : undefined}
                    />
                    <circle cx={geo.head[0]} cy={geo.head[1]} r={3.5} fill={color} />
                  </g>
                )
              })}
            </svg>

            {/* Labels on the selected agent's data lines. */}
            {sel &&
              visible
                .filter((e) => touches(e, sel) && e.kind !== 'order')
                .map((e) => {
                  const geo = edgePath(e, pos)
                  if (!geo) return null
                  const c = edgeColor(e.kind, palette)
                  const passed = e.state === 'passed'
                  return (
                    <span
                      key={`l-${e.id}`}
                      data-testid="edge-label"
                      className="absolute whitespace-nowrap rounded-full font-semibold text-[11px] px-[9px] py-[3px] max-w-[220px] overflow-hidden text-ellipsis"
                      title={stateLine(e)}
                      style={{
                        zIndex: 3,
                        left: geo.mid[0],
                        top: geo.mid[1],
                        transform: 'translate(-50%, -50%)',
                        background: 'rgb(var(--ground))',
                        color: passed ? c : tokenColor('muted'),
                        border: `1px solid ${passed ? c : 'var(--hairline-strong)'}`,
                      }}
                    >
                      {edgeLabel(e)}
                    </span>
                  )
                })}

            {nodeIds.map((id) => {
              const p = pos[id]
              const s = statusOf(id)
              const a = agents[id]
              const on = sel === id
              const gold = id === PSEUDO.critic || id === PSEUDO.report
              const running = s.key === 'running'
              const dot = s.key === 'none' ? tokenColor('mark-warn') : STATUS_MARK[s.key]
              const meta =
                PSEUDO_META[id] ??
                (a && isDone(a.status) && a.status !== 'skipped'
                  ? `${a.findings_count} found`
                  : a?.status === 'skipped'
                    ? a.skip_reason || 'skipped'
                    : '–')
              const faded = s.key === 'waiting' || s.key === 'skipped'
              const dim = !adjacent(id)
              return (
                <button
                  key={id}
                  type="button"
                  data-node={id}
                  aria-pressed={on}
                  aria-label={`${nameOf(id)}, ${s.label}${a && isDone(a.status) && a.status !== 'skipped' ? `, ${a.findings_count} found` : ''}`}
                  onClick={() => pick(id)}
                  className={`absolute flex flex-col justify-between gap-1 px-[11px] py-2 text-left text-ink rounded-[12px] cursor-pointer transition-opacity ${
                    running && !on ? 'td-flow-glow' : ''
                  }`}
                  style={{
                    left: p.x,
                    top: p.y,
                    width: NODE_W,
                    height: NODE_H,
                    zIndex: 2,
                    background: running
                      ? 'linear-gradient(135deg, rgb(var(--accent) / 0.45), rgb(var(--accent) / 0.15))'
                      : 'linear-gradient(135deg, rgb(var(--raised)), rgb(var(--panel)))',
                    border: `1px solid ${on ? palette.champagne : gold ? `${palette.champagne}73` : 'var(--hairline-strong)'}`,
                    boxShadow: on ? `0 0 0 3px ${palette.champagne}38, 0 8px 24px rgba(0,0,0,0.25)` : 'var(--shadow-card)',
                    opacity: dim ? 0.35 : faded ? 0.6 : 1,
                  }}
                >
                  <span className="flex items-center gap-[6px] min-w-0">
                    <span
                      className="w-3 h-3 rounded-full inline-flex items-center justify-center flex-none"
                      style={{ border: `2px solid ${dot}` }}
                    >
                      <span className={`w-1 h-1 rounded-full ${running ? 'td-flow-pulse' : ''}`} style={{ background: dot }} />
                    </span>
                    <span className="text-[12px] font-semibold whitespace-nowrap overflow-hidden text-ellipsis">{nameOf(id)}</span>
                  </span>
                  <span className="flex justify-between gap-[6px] text-[10px] min-w-0">
                    <span className="tabular-nums text-body whitespace-nowrap overflow-hidden text-ellipsis">{meta}</span>
                    <span className="font-semibold tracking-[0.06em] uppercase flex-none" style={{ color: s.key === 'none' ? tokenColor('warn') : STATUS_COLOR[s.key] }}>
                      {s.label}
                    </span>
                  </span>
                </button>
              )
            })}

            <button
              type="button"
              data-node={PSEUDO.signal}
              aria-pressed={sel === PSEUDO.signal}
              onClick={() => pick(PSEUDO.signal)}
              className="absolute rounded-full flex items-center justify-center gap-2 cursor-pointer transition-opacity"
              style={{
                left: SIGNAL_BAND.left,
                top: SIGNAL_BAND.top,
                width: SIGNAL_BAND.width,
                height: SIGNAL_BAND.height,
                zIndex: 2,
                border: `1px solid ${sel === PSEUDO.signal ? palette.champagne : 'rgb(var(--accent-text) / 0.45)'}`,
                background: 'linear-gradient(90deg, rgb(var(--accent) / 0.10), rgb(var(--accent) / 0.30), rgb(var(--accent) / 0.10))',
                boxShadow: '0 0 24px rgb(var(--accent) / 0.35)',
                opacity: adjacent(PSEUDO.signal) ? 1 : 0.35,
              }}
            >
              <span className="font-semibold text-[10px] tracking-[0.16em] uppercase text-accent-text">Shared findings signal</span>
              <span className="text-[11px] text-body">which files earlier agents flagged</span>
            </button>
          </div>
          </div>
        </div>

        {/* Side panel */}
        <aside
          className="bg-raised border border-hairline rounded-[20px] px-5 py-[18px] flex flex-col gap-3"
          aria-live="polite"
          data-testid="flow-panel"
        >
          {sel ? (
            <>
              <span className="font-semibold text-[11px] tracking-[0.16em] uppercase text-muted">{statusOf(sel).label}</span>
              <span className="text-[18px] font-semibold text-ink">{nameOf(sel)}</span>
              {selStory && selAgent && (
                <div data-testid="flow-panel-reason" className="flex flex-col gap-2 rounded-lg border border-hairline bg-fill-subtle px-3 py-2">
                  <span className="text-[13px] font-semibold text-ink">{agentHeadline(selAgent)}</span>
                  {onInspect && (
                    <button
                      type="button"
                      onClick={() => onInspect(selAgent.agent_id)}
                      aria-haspopup="dialog"
                      className="self-start font-semibold text-[12px] text-accent-text hover:underline"
                    >
                      See what happened
                    </button>
                  )}
                </div>
              )}
              {sel === PSEUDO.critic && criticFailed && (
                <div data-testid="flow-panel-reason" className="rounded-lg border border-hairline bg-fill-subtle px-3 py-2 text-[13px] font-semibold text-ink">
                  The Critic failed, so its review (removing duplicates and grouping findings) may not have run.
                </div>
              )}
              <span className="text-[13px] leading-[1.55] text-body">{WHAT[sel] ?? agentDescription(sel)}</span>
              <div className="flex flex-col gap-2">
                <span className="font-semibold text-[10px] tracking-[0.16em] uppercase text-muted">Receives</span>
                {selEdgesIn.length === 0 ? (
                  <PanelRow name="Nothing" data="This reads only the code." state="" color={tokenColor('faint')} />
                ) : (
                  selEdgesIn.map((e) => (
                    <PanelRow key={e.id} name={nameOf(e.from)} data={e.info} state={stateLine(e)} color={stateColor(e, palette)} />
                  ))
                )}
                <span className="font-semibold text-[10px] tracking-[0.16em] uppercase text-muted mt-1">Passes on</span>
                {selEdgesOut.length === 0 ? (
                  <PanelRow name="Nothing" data="This is the end of the line." state="" color={tokenColor('faint')} />
                ) : (
                  selEdgesOut.map((e) => (
                    <PanelRow key={e.id} name={nameOf(e.to)} data={e.info} state={stateLine(e)} color={stateColor(e, palette)} />
                  ))
                )}
              </div>
              {canViewFindings && (
                <Link
                  to={selCategory ? `/scans/${scanId}/findings?category=${selCategory}` : `/scans/${scanId}/findings`}
                  className="self-start font-semibold text-[12px] text-accent-text hover:underline"
                >
                  View findings
                </Link>
              )}
            </>
          ) : (
            <>
              <span className="font-semibold text-[11px] tracking-[0.16em] uppercase text-muted">Select an agent</span>
              <span className="text-[13px] leading-[1.55] text-body">
                Click any card to light up its lines and see what it receives and passes on. Press Escape to clear.
              </span>
            </>
          )}
        </aside>
      </div>
    </div>
  )
}

function PanelRow({ name, data, state, color }: { name: string; data: string; state: string; color: string }) {
  return (
    <div className="flex flex-col gap-[2px] px-[10px] py-2 rounded-lg bg-fill-subtle" data-testid="panel-row">
      <span className="text-[12px] font-semibold text-ink">{name}</span>
      <span className="text-[12px] text-body">{data}</span>
      {state && (
        <span className="text-[11px] font-semibold" style={{ color }}>
          {state}
        </span>
      )}
    </div>
  )
}
